import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Search, 
  Plus, 
  Users, 
  TrendingUp, 
  DollarSign, 
  Activity,
  AlertTriangle,
  Calendar,
  Settings,
  Bell
} from 'lucide-react';
import Fuse from 'fuse.js';
import GroupCard from '../components/common/GroupCard';
import groupService from '../api/groupService';

const ModernDashboard = () => {
  const { currentUser } = useAuth();
  const { adminGroup, adminGroups, loading: contextLoading, adminHadCommunities, error } = useGroup();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);

  // Check if admin already has a community
  const hasExistingCommunity = adminGroup || 
                               (adminGroups && adminGroups.length > 0) || 
                               (currentUser?.role === 'admin' && adminHadCommunities);

  // Mock data for metrics (you can replace with real data from your API)
  const metrics = {
    totalMembers: adminGroups ? adminGroups.reduce((total, group) => total + (group.member_count || 0), 0) : 0,
    activeGroups: adminGroups ? adminGroups.length : 0,
    pendingRequests: 5, // This would come from your API
    totalRevenue: 8945 // This would come from your Stripe integration
  };

  // Configure Fuse.js for fuzzy searching
  const fuseOptions = {
    keys: [
      { name: 'name', weight: 0.7 },
      { name: 'description', weight: 0.3 }
    ],
    threshold: 0.4,
    includeScore: true,
    minMatchCharLength: 2,
    ignoreLocation: true,
    useExtendedSearch: true,
    findAllMatches: true,
    tokenize: true,
    matchAllTokens: false
  };

  // Create a Fuse instance when groups change
  const fuse = useMemo(() => {
    return new Fuse(allGroups, fuseOptions);
  }, [allGroups]);

  // Create debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId = null;
      return (query) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        
        if (!query.trim()) {
          setSearchResults([]);
          setShowNoResults(false);
          setLoading(false);
          return;
        }
        
        setLoading(true);
        timeoutId = setTimeout(async () => {
          try {
            const response = await groupService.searchGroups(query);
            
            // Store all groups for fuzzy search
            setAllGroups(response.data || []);
            
            if (response.data.length === 0) {
              setSearchResults([]);
              setShowNoResults(true);
            } else {
              // Apply fuzzy search to rank results by relevance
              const fuzzyResults = fuse.search(query);
              
              // If fuzzy search returns results, use them; otherwise fall back to API results
              if (fuzzyResults.length > 0) {
                const sortedResults = fuzzyResults.map(result => result.item);
                setSearchResults(sortedResults);
                setShowNoResults(false);
              } else {
                setSearchResults(response.data);
                setShowNoResults(response.data.length === 0);
              }
            }
          } catch (error) {
            console.error('Error searching groups:', error);
            setSearchResults([]);
            setShowNoResults(true);
          } finally {
            setLoading(false);
          }
        }, 300);
      };
    })(),
    [fuse]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    debouncedSearch(newQuery);
  };

  // Handle clearing the search
  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowNoResults(false);
    setLoading(false);
  };

  // Handle create community button click
  const handleCreateCommunity = () => {
    navigate('/create-group');
  };

  // Show loading UI for groups loading
  if (contextLoading) {
    return (
      <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded-lg w-64"></div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            Hello {currentUser?.name?.split(' ')[0]} 👋
          </h2>
          <p className="text-muted-foreground">
            Here's what's happening with your communities today.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon">
            <Bell className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon">
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Search Section */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search communities..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-8"
          />
        </div>
        {hasExistingCommunity && (
          <Button onClick={handleCreateCommunity}>
            <Plus className="mr-2 h-4 w-4" />
            New Community
          </Button>
        )}
      </div>

      {/* Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalMembers.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+2.1% from last month</span>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Groups</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.activeGroups}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+1 this week</span>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${metrics.totalRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+5.2% from last month</span>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Join Requests</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.pendingRequests}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-orange-600">Pending approval</span>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        {/* Communities Overview */}
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>Your Communities</CardTitle>
            <CardDescription>
              Manage and monitor your active communities
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(adminGroup || (adminGroups && adminGroups.length > 0)) && (
              <div className="space-y-4">
                {adminGroup && (
                  <div className="flex items-center space-x-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex-1">
                      <h3 className="font-medium">{adminGroup.name}</h3>
                      <p className="text-sm text-muted-foreground">{adminGroup.description}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary">{adminGroup.member_count || 0} members</Badge>
                      <Button size="sm" onClick={() => navigate(`/group/${adminGroup.id}`)}>
                        Open
                      </Button>
                    </div>
                  </div>
                )}
                {!adminGroup && adminGroups && adminGroups.map(group => (
                  <div key={group.id} className="flex items-center space-x-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex-1">
                      <h3 className="font-medium">{group.name}</h3>
                      <p className="text-sm text-muted-foreground">{group.description}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary">{group.member_count || 0} members</Badge>
                      <Button size="sm" onClick={() => navigate(`/group/${group.id}`)}>
                        Open
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {!hasExistingCommunity && (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No communities yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first community to get started with YesodCoPilot
                </p>
                <Button onClick={handleCreateCommunity}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Your First Community
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="col-span-3">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <div className="flex-1">
                <p className="text-sm font-medium">New member joined</p>
                <p className="text-xs text-muted-foreground">2 minutes ago</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <div className="flex-1">
                <p className="text-sm font-medium">Payment received</p>
                <p className="text-xs text-muted-foreground">1 hour ago</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="flex-1">
                <p className="text-sm font-medium">Join request pending</p>
                <p className="text-xs text-muted-foreground">3 hours ago</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Search Results</CardTitle>
            <CardDescription>
              Communities matching "{searchQuery}"
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {searchResults.map(group => (
                <div key={group.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                  <h3 className="font-medium mb-2">{group.name}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{group.description}</p>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline">{group.member_count || 0} members</Badge>
                    <Button size="sm" variant="outline" disabled>
                      Admin View Only
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4 text-blue-600" />
                <p className="text-sm text-blue-800 dark:text-blue-400">
                  As an admin, you can view communities but cannot join them. You can only manage your own community.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card>
          <CardContent className="p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2 text-muted-foreground">Searching...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {showNoResults && !loading && (
        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <Search className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
              <h3 className="font-medium mb-1">No communities found</h3>
              <p className="text-sm text-muted-foreground">
                No communities found for "{searchQuery}"
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ModernDashboard;
